import { EventWithDetail, OrgFullDetail } from "@garther/interfaces";
import { Avatar, Box, Button, Image, Input, Progress, TabPanel, Table, Tabs } from "@garther/ui/components";
import { refLink, toast, useI18n } from "@garther/ui/libs";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Calendar, Clock, Copy, MapPin } from "lucide-react";
import { useMemo, useState } from "react";

import { RawLayout } from "@/components/layout";
import { bookingApiClient } from "@/libs/api";
import { CLIENT_BASE_URL } from "@/libs/configs";

interface EventDetailPageProps {
  org: OrgFullDetail;
  event: EventWithDetail;
}

export function EventDetailPage({ org, event }: EventDetailPageProps) {
  const { formatDate, t, formatCredit } = useI18n();
  const [activeTab, setActiveTab] = useState("info");
  const navigate = useNavigate();

  // Format date and time
  const eventDate = new Date(event.date);
  const formattedDate = formatDate(eventDate, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });
  const formattedTime = formatDate(eventDate, {
    hour: "2-digit",
    minute: "2-digit"
  });

  const { data: bookings } = useQuery({
    queryKey: ["booking", event.id],
    queryFn: async () => bookingApiClient.getBookingsByEventId(event.id),
    enabled: !!event.id,
    staleTime: 5 * 60 * 1000
  });

  const referralLink = useMemo(() => {
    if (org.account?.referralCode)
      return refLink(`${CLIENT_BASE_URL}/event/${event.id}`, org.account?.referralCode);
    return "";
  }, [org.account?.referralCode, event.id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success(t("credits.wallet.referralLinkCopied"));
  };

  return (
    <RawLayout>
      <div className="relative min-h-screen bg-background-primary">
        {/* Banner Image */}
        <div className="relative w-full aspect-[21/9] max-h-[400px] bg-background-secondary">
          <Image
            src={event.banner || event.featuredImages[0]}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title & Quick Info */}
              <div className="space-y-4">
                <h1 className="text-responsive-2xl font-medium text-text-primary">{event.title}</h1>

                <div className="flex flex-wrap gap-4 text-responsive-base">
                  <div className="flex items-center gap-2 text-text-secondary">
                    <Calendar className="h-5 w-5" />
                    <span>{formattedDate}</span>
                  </div>
                  <div className="flex items-center gap-2 text-text-secondary">
                    <Clock className="h-5 w-5" />
                    <span>{formattedTime}</span>
                  </div>
                  <div className="flex items-center gap-2 text-text-secondary">
                    <MapPin className="h-5 w-5" />
                    <span>{event.location?.address || "HCM, Vietnam"}</span>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <Tabs
                items={[
                  { id: "info", label: "Information" },
                  { id: "attendee", label: "Attendee" }
                ]}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                variant="underline"
                className="w-full px-0"
                size="md"
              />

              <div className="mt-6">
                <TabPanel isActive={activeTab === "info"}>
                  <div className="space-y-6">
                    {/* Referral Link */}
                    <div className="space-y-0">
                      <p className="text-text-secondary text-responsive-sm font-medium">
                        {t("credits.wallet.yourReferralLink")}
                      </p>
                      <div className="flex gap-2 w-full">
                        <Input
                          containerClassName="flex-1"
                          value={referralLink}
                          readOnly
                          className="bg-background-secondary border-border text-text-primary text-responsive-base"
                        />
                        <Button
                          onClick={handleCopyLink}
                          variant="outline"
                          className="border-border text-text-primary hover:bg-background-highlight shrink-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-responsive-md font-normal text-text-secondary px-4 md:px-6">
                        {t("eventDetail.aboutThisEvent").toUpperCase()}
                      </h3>
                      <Box className="space-y-4">
                        <p
                          className="text-text-primary text-reponsive-base"
                          dangerouslySetInnerHTML={{ __html: event.description }}
                        />
                        <div className="space-y-2 bg-background-highlight rounded-sm p-4">
                          <h4 className="text-responsive-sm font-medium text-text-secondary">
                            {t("eventDetail.hostedBy")}
                          </h4>
                          <div
                            className="flex items-center gap-4 w-fit transition-all duration-300 transform hover:scale-[1.04] cursor-pointer"
                            onClick={() => navigate({ to: "/org/$orgId", params: { orgId: event.host.id } })}
                          >
                            <Avatar
                              name={event.host.name}
                              src={event.host.avatar || undefined}
                              size="lg"
                              className="h-10 w-10 flex-shrink-0"
                            />
                            <div>
                              <p className="text-text-primary text-responsive-md font-normal">
                                {event.host.name}
                              </p>
                              <p className="text-text-secondary text-responsive-sm font-medium">
                                {event.host.tagline}
                              </p>
                            </div>
                          </div>
                        </div>
                      </Box>
                    </div>
                  </div>
                </TabPanel>

                <TabPanel isActive={activeTab === "attendee"}>
                  <div>
                    <p className="text-responsive-lg font-normal text-text-primary">Event Attendees</p>
                    <p className="text-responsive-base font-normal text-text-secondary">
                      List of users registered to this event
                    </p>
                  </div>
                  <div className="space-y-4">
                    {bookings && (
                      <Table
                        data={bookings}
                        columns={[
                          {
                            key: "name",
                            title: "Name",
                            dataIndex: "user.name"
                          },
                          {
                            key: "email",
                            title: "Email",
                            dataIndex: "user.email"
                          },
                          {
                            key: "ticket",
                            title: "Ticket",
                            dataIndex: "ticket.title"
                          },
                          {
                            key: "quantity",
                            title: "Quantity",
                            dataIndex: "quantity"
                          },
                          {
                            key: "createdAt",
                            title: "Created At",
                            dataIndex: "user.createdAt",
                            formatter: (value: Date) => formatDate(value)
                          }
                        ]}
                      />
                    )}
                  </div>
                </TabPanel>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Pricing Card */}
                <Box className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-responsive-md font-normal text-text-primary">Stats</h3>
                    <div>
                      <p className="text-responsive-sm font-medium text-text-secondary">Total registered</p>
                      <p className="text-responsive-base font-normal text-text-primary">
                        {event.taken}
                        <span className="text-responsive-sm font-medium text-text-secondary">
                          /{event.cap}
                        </span>
                      </p>
                    </div>
                    {event.ticketTiers.map((t, i) => (
                      <div key={i} className="ml-3">
                        <p className="text-responsive-sm font-medium text-text-secondary">{t.title}</p>
                        <Progress value={(t.taken / t.cap) * 100} />
                        <p className="text-responsive-base font-normal text-text-primary">
                          {t.taken}
                          <span className="text-responsive-sm font-medium text-text-secondary">/{t.cap}</span>
                        </p>
                      </div>
                    ))}
                    <div>
                      <p className="text-responsive-sm font-medium text-text-secondary">Total Revenue</p>
                      <p className="text-responsive-base font-normal text-text-primary">
                        {formatCredit(event.ticketTiers.reduce((a, b) => a + b.taken * b.price, 0))}
                      </p>
                    </div>
                  </div>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
    </RawLayout>
  );
}
